import React, { useEffect } from 'react';
import { VStack, HStack, Box, Text, Button, Icon } from '@chakra-ui/react';
import { LuArrowRight } from 'react-icons/lu';
import { useFormContext } from '../context/FormContext';
import { setFrequency, setAmount, setCustomAmount, nextStep, prevStep } from '../context/formActions';
import { canContinueFromStep } from '../validation';
import { AmountSelect } from '../AmountSelect';
import { getAmountTiers, donateEvent } from '@kuow/core';
import { CheckboxCard } from '../../primitives/CheckboxCard';
import { CheckboxCircle } from '../../primitives/CheckboxCircle';
import { useDonationFormUi } from '../context/UiContext';

export const MonthlyUpsellStep: React.FC = () => {
  const { state, dispatch } = useFormContext();
  const { showStepNavigator } = useDonationFormUi();
  const { formDetails, formData, validation } = state;
  const [keepOneTimeSelected, setKeepOneTimeSelected] = React.useState(true);
  const [monthlyAmount, setMonthlyAmount] = React.useState<number | null>(null);
  const [monthlyCustomAmount, setMonthlyCustomAmount] = React.useState<number | null>(null);

  const initialFormDataRef = React.useRef<typeof formData | null>(null);
  initialFormDataRef.current ??= formData;
  const originalFormData = initialFormDataRef.current;

  useEffect(() => {
    donateEvent('Step Evergreen Upsell', state.placement);
  }, [state.placement]);

  if (!formDetails) {
    return null;
  }

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setMonthlyCustomAmount(null);
      dispatch(setCustomAmount(null));
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue > 0) {
        setMonthlyCustomAmount(numValue);
        dispatch(setCustomAmount(numValue));
      }
    }
  };

  const tierItems = getAmountTiers(formDetails, 'monthly', { limit: 4 });

  const selectedAmountValue =
    monthlyCustomAmount !== null
      ? 'custom'
      : monthlyAmount !== null
      ? tierItems.find((item) => monthlyAmount === item.numericAmount)?.value ?? ''
      : '';

  const customAmountDisplay =
    monthlyCustomAmount !== null && monthlyCustomAmount > 0
      ? monthlyCustomAmount
      : '';

  const handleAmountChange = (value: string | null) => {
    if (value === 'custom') {
      if (monthlyCustomAmount === null) {
        setMonthlyCustomAmount(0);
        dispatch(setCustomAmount(0));
      }
      setMonthlyAmount(null);
      dispatch(setFrequency('monthly'));
      setKeepOneTimeSelected(false);
      donateEvent('Evergreen Upsell', state.placement);
      return;
    }

    const item = tierItems.find((tierItem) => tierItem.value === value);
    if (item) {
      setMonthlyCustomAmount(null);
      setMonthlyAmount(item.numericAmount);
      dispatch(setCustomAmount(null));
      dispatch(setAmount(item.numericAmount));
      dispatch(setFrequency('monthly'));
      setKeepOneTimeSelected(false);
      donateEvent('Evergreen Upsell', state.placement);
    }
  };

  const handleKeepOneTimeClick = () => {
    setKeepOneTimeSelected(true);
    setMonthlyAmount(null);
    setMonthlyCustomAmount(null);

    if (originalFormData) {
      dispatch(setFrequency(originalFormData.frequency));

      if (originalFormData.customAmount !== null) {
        dispatch(setCustomAmount(originalFormData.customAmount));
      } else if (originalFormData.amount !== null) {
        dispatch(setAmount(originalFormData.amount));
      }
    }
  };

  const handleContinue = () => {
    dispatch(nextStep());
  };

  const showBackButton = !showStepNavigator;

  const originalOneTimeAmount =
    originalFormData?.customAmount && originalFormData.customAmount > 0
      ? originalFormData.customAmount
      : originalFormData?.amount && originalFormData.amount > 0
      ? originalFormData.amount
      : null;

  const evergreenHeadline = formDetails.evergreen_cta_headline && formDetails.evergreen_cta_headline.trim().length > 0 ? formDetails.evergreen_cta_headline.trim() : 'You can make an even bigger impact as an Evergreen Member!';
  const evergreenDescription = formDetails.evergreen_cta_description && formDetails.evergreen_cta_description.trim().length > 0 ? formDetails.evergreen_cta_description.trim() : 'Monthly Evergreen donations provide steady support for the station. Help KUOW continue to produce quality, independent journalism into the future!';

  return (
    <VStack gap={6} align="stretch" textAlign="center">
      <Box>
        <Text fontSize="md" fontWeight="semibold" mb={1.5}>
          {evergreenHeadline}
        </Text>
        <Text fontSize="xs" color="fg.muted">
          {evergreenDescription}
        </Text>
      </Box>

      <Box>
        <Text fontWeight="semibold" fontSize="sm" mb={1.5}>
          Monthly Amount
        </Text>
        <AmountSelect
          items={tierItems}
          selectedValue={selectedAmountValue}
          onChange={handleAmountChange}
          customAmountValue={customAmountDisplay}
          onCustomAmountChange={handleCustomAmountChange}
          frequency="monthly"
        />
      </Box>

      <Box>
        <CheckboxCard isSelected={keepOneTimeSelected} onClick={handleKeepOneTimeClick}>
          <HStack gap={3} align="center">
            <CheckboxCircle isSelected={keepOneTimeSelected} />
            <Box textAlign="left">
              <Text fontSize="sm" fontWeight="semibold">
                Keep One-Time Gift
              </Text>
              {originalOneTimeAmount !== null && (
                <Text fontSize="xs" color="fg.muted">
                  I&apos;d like to donate{' '}
                  <Text as="span" fontWeight="bold">
                    ${originalOneTimeAmount}
                  </Text>{' '}
                  today.
                </Text>
              )}
            </Box>
          </HStack>
        </CheckboxCard>
      </Box>

      <Button
        colorPalette="river"
        size="md"
        onClick={handleContinue}
        disabled={!canContinueFromStep('monthly-upsell', validation)}
        alignSelf="center"
        px={12}
        py={2}
        whiteSpace="normal"
        textAlign="center"
        height="auto"
        minH="40px"
      >
        Continue
        <Icon as={LuArrowRight} flexShrink={0} />
      </Button>

      {showBackButton ? (
        <Button
          colorPalette="river"
          variant="subtle"
          size="xs"
          onClick={() => dispatch(prevStep())}
          alignSelf="center"
        >
          Back
        </Button>
      ) : null}
    </VStack>
  );
};


